import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  CheckCircle,
  FileText,
  User,
  Home,
  CreditCard,
  File,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Bell,
  PenLine,
  Shield,
  Receipt,
  Building2
} from 'lucide-react';
import { NsKey } from 'enums/NsKey';
import {
  ActivityFeedEntityLinkDto,
  ActivityMessageSegmentDto,
  EntityTypeEnum,
  GranularityLevelEnum,
  HistoryPriorityEnum,
  HistoryEventDto,
  HistoryEventGroupDto,
  HistoryModificationDetailDto
} from 'api/activity-feed';
import { useActivityTimeline } from '../../hooks/useActivityTimeline';
import { ActivitySegments } from '../ActivitySegments';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { formatRelativeTime } from '../../mocks/activityFeedMockData';
import { getIconByKey } from '../../utils/iconMapping';

// ============================================
// ENTITY NAVIGATION UTILS
// ============================================

/**
 * Generates the navigation URL for an entity based on its type
 */
const getEntityUrl = (
  entityType: EntityTypeEnum,
  entityId: string,
  entities?: ActivityFeedEntityLinkDto[]
): string | null => {
  switch (entityType) {
    case EntityTypeEnum.Contrat:
      return `/mes-contrats/details/${entityId}`;

    case EntityTypeEnum.Devis:
      // For Devis, we need the riskId (first param) and devisId (second param)
      const risqueAssure = entities?.find(
        (e) => e.entityType === EntityTypeEnum.RisqueAssure
      );
      if (risqueAssure) {
        return `/simulation/contractualisation/${risqueAssure.entityId}/${entityId}`;
      }
      return null;

    case EntityTypeEnum.Personne:
      return `/mes-clients/${entityId}`;

    case EntityTypeEnum.RisqueAssure:
    case EntityTypeEnum.BienImmobilier:
    case EntityTypeEnum.Locataire:
    case EntityTypeEnum.Mandat:
    case EntityTypeEnum.Quittance:
    case EntityTypeEnum.Document:
    case EntityTypeEnum.Courtier:
    case EntityTypeEnum.Garantie:
    default:
      return null;
  }
};

// ============================================
// ENTITY LINK COMPONENT
// ============================================

interface EntityLinkProps {
  entityType: EntityTypeEnum;
  entityId: string;
  displayText: string;
  entities?: ActivityFeedEntityLinkDto[];
}

const EntityLink: React.FC<EntityLinkProps> = ({
  entityType,
  entityId,
  displayText,
  entities
}) => {
  const navigate = useNavigate();
  // Liens du flux d'activité / notifications : matrice centrale (Courtier uniquement).
  const canFollowLinks = useRoleCan(Capability.ActivityFeedLinks);
  const url = canFollowLinks ? getEntityUrl(entityType, entityId, entities) : null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (url) {
      navigate(url);
    }
  };

  if (!url) {
    return (
      <span className="font-medium text-primary">
        {displayText}
      </span>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="font-medium text-primary hover:text-primary/80 hover:underline"
    >
      {displayText}
    </button>
  );
};

// ============================================
// MESSAGE PARSER
// ============================================

interface ParsedMessagePart {
  type: 'text' | 'entity';
  content: string;
  entityType?: EntityTypeEnum;
  entityId?: string;
}

/**
 * Parses a message containing {EntityType:Guid} references
 * and returns an array of text and entity parts
 */
const parseMessage = (
  message: string,
  entities: ActivityFeedEntityLinkDto[]
): ParsedMessagePart[] => {
  const parts: ParsedMessagePart[] = [];
  const entityPattern = /\{([^:}]+):([^}]+)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = entityPattern.exec(message)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: message.slice(lastIndex, match.index)
      });
    }

    const entityType = match[1] as EntityTypeEnum;
    const entityId = match[2];

    // Always create an entity link for recognized patterns
    // Even if not in entities array, we can still navigate and show a fallback label
    parts.push({
      type: 'entity',
      content: match[0],
      entityType,
      entityId
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < message.length) {
    parts.push({
      type: 'text',
      content: message.slice(lastIndex)
    });
  }

  return parts;
};

/**
 * Gets the display label for an entity from the entities array
 */
const getEntityLabel = (
  entities: ActivityFeedEntityLinkDto[],
  entityType: EntityTypeEnum,
  entityId: string
): string => {
  const entity = entities.find(
    (e) => e.entityType === entityType && e.entityId === entityId
  );

  if (entity?.entityLabel) {
    return entity.entityLabel;
  }

  // Fallback labels based on entity type
  switch (entityType) {
    case EntityTypeEnum.Contrat:
      return 'Contrat';
    case EntityTypeEnum.Devis:
      return 'Devis';
    case EntityTypeEnum.Personne:
      return 'Personne';
    case EntityTypeEnum.BienImmobilier:
      return 'Bien immobilier';
    case EntityTypeEnum.Locataire:
      return 'Locataire';
    case EntityTypeEnum.Document:
      return 'Document';
    case EntityTypeEnum.Mandat:
      return 'Mandat';
    default:
      return 'Élément';
  }
};

// ============================================
// RENDERED MESSAGE COMPONENT
// ============================================

interface RenderedMessageProps {
  message: string;
  entities?: ActivityFeedEntityLinkDto[];
  /** Message riche du back : prioritaire sur le parsing du message brut. */
  segments?: ActivityMessageSegmentDto[] | null;
  /** Dossier porteur (cible des entités sans page propre). */
  riskId?: string;
  /** Projet d'emprunt porteur — bascule les liens sur les URLs borrower. */
  borrowerProjectId?: string;
}

const RenderedMessage: React.FC<RenderedMessageProps> = ({
  message,
  entities = [],
  segments,
  riskId,
  borrowerProjectId
}) => {
  const messageParts = useMemo(
    () => parseMessage(message, entities),
    [message, entities]
  );

  if (segments && segments.length > 0) {
    return (
      <ActivitySegments
        segments={segments}
        riskId={riskId}
        borrowerProjectId={borrowerProjectId}
      />
    );
  }

  return (
    <>
      {messageParts.map((part, index) => {
        if (part.type === 'entity' && part.entityType && part.entityId) {
          const displayText = getEntityLabel(
            entities,
            part.entityType,
            part.entityId
          );
          return (
            <EntityLink
              key={`${part.entityId}-${index}`}
              entityType={part.entityType}
              entityId={part.entityId}
              displayText={displayText}
              entities={entities}
            />
          );
        }
        return <span key={index}>{part.content}</span>;
      })}
    </>
  );
};

// ============================================
// GRANULARITY SELECTOR
// ============================================

interface GranularitySelectorProps {
  value: GranularityLevelEnum;
  onChange: (level: GranularityLevelEnum) => void;
}

const GranularitySelector: React.FC<GranularitySelectorProps> = ({
  value,
  onChange
}) => {
  const { t } = useTranslation(NsKey.Common);

  const options = [
    {
      level: GranularityLevelEnum.Resume,
      label: t('ACTIVITY_TIMELINE.GRANULARITY.RESUME')
    },
    {
      level: GranularityLevelEnum.Standard,
      label: t('ACTIVITY_TIMELINE.GRANULARITY.STANDARD')
    },
    {
      level: GranularityLevelEnum.Complete,
      label: t('ACTIVITY_TIMELINE.GRANULARITY.COMPLETE')
    }
  ];

  return (
    <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
      {options.map((option) => (
        <button
          key={option.level}
          onClick={() => onChange(option.level)}
          className={clsx(
            'px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200',
            value === option.level
              ? 'bg-white text-primary shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

// ============================================
// PRIORITY & ENTITY STYLES
// ============================================

const getPriorityStyles = (priority: HistoryPriorityEnum | string) => {
  // Normalize priority to handle both French and English values from API
  const normalizedPriority = String(priority).toLowerCase();

  if (normalizedPriority === 'urgent' || normalizedPriority === 'urgente') {
    return {
      bgColor: 'bg-red-500',
      ringColor: 'ring-red-100',
      borderColor: 'border-red-200',
      lightBg: 'bg-red-50',
      textColor: 'text-red-700',
      icon: Bell
    };
  }

  if (normalizedPriority === 'high' || normalizedPriority === 'haute' || normalizedPriority === 'élevée') {
    return {
      bgColor: 'bg-orange-500',
      ringColor: 'ring-orange-100',
      borderColor: 'border-orange-200',
      lightBg: 'bg-orange-50',
      textColor: 'text-orange-700',
      icon: AlertCircle
    };
  }

  if (normalizedPriority === 'normal' || normalizedPriority === 'normale') {
    return {
      bgColor: 'bg-primary',
      ringColor: 'ring-primary/20',
      borderColor: 'border-primary/20',
      lightBg: 'bg-primary/5',
      textColor: 'text-primary',
      icon: CheckCircle
    };
  }

  // Low, Basse, or default
  return {
    bgColor: 'bg-gray-400',
    ringColor: 'ring-gray-100',
    borderColor: 'border-gray-200',
    lightBg: 'bg-gray-50',
    textColor: 'text-gray-600',
    icon: CheckCircle
  };
};

const getEntityStyles = (entityType: EntityTypeEnum | null | undefined) => {
  switch (entityType) {
    case EntityTypeEnum.Personne:
      return {
        bgColor: 'bg-purple-500',
        ringColor: 'ring-purple-100',
        lightBg: 'bg-purple-50',
        borderColor: 'border-purple-200',
        textColor: 'text-purple-700',
        icon: User
      };
    case EntityTypeEnum.Locataire:
      return {
        bgColor: 'bg-teal-500',
        ringColor: 'ring-teal-100',
        lightBg: 'bg-teal-50',
        borderColor: 'border-teal-200',
        textColor: 'text-teal-700',
        icon: User
      };
    case EntityTypeEnum.BienImmobilier:
      return {
        bgColor: 'bg-amber-500',
        ringColor: 'ring-amber-100',
        lightBg: 'bg-amber-50',
        borderColor: 'border-amber-200',
        textColor: 'text-amber-700',
        icon: Home
      };
    case EntityTypeEnum.Document:
      return {
        bgColor: 'bg-indigo-500',
        ringColor: 'ring-indigo-100',
        lightBg: 'bg-indigo-50',
        borderColor: 'border-indigo-200',
        textColor: 'text-indigo-700',
        icon: File
      };
    case EntityTypeEnum.Contrat:
      return {
        bgColor: 'bg-emerald-500',
        ringColor: 'ring-emerald-100',
        lightBg: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        textColor: 'text-emerald-700',
        icon: FileText
      };
    case EntityTypeEnum.Devis:
      return {
        bgColor: 'bg-blue-500',
        ringColor: 'ring-blue-100',
        lightBg: 'bg-blue-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-700',
        icon: FileText
      };
    case EntityTypeEnum.Mandat:
      return {
        bgColor: 'bg-orange-500',
        ringColor: 'ring-orange-100',
        lightBg: 'bg-orange-50',
        borderColor: 'border-orange-200',
        textColor: 'text-orange-700',
        icon: PenLine
      };
    case EntityTypeEnum.Garantie:
      return {
        bgColor: 'bg-cyan-500',
        ringColor: 'ring-cyan-100',
        lightBg: 'bg-cyan-50',
        borderColor: 'border-cyan-200',
        textColor: 'text-cyan-700',
        icon: Shield
      };
    case EntityTypeEnum.Quittance:
      return {
        bgColor: 'bg-rose-500',
        ringColor: 'ring-rose-100',
        lightBg: 'bg-rose-50',
        borderColor: 'border-rose-200',
        textColor: 'text-rose-700',
        icon: Receipt
      };
    case EntityTypeEnum.Courtier:
      return {
        bgColor: 'bg-slate-500',
        ringColor: 'ring-slate-100',
        lightBg: 'bg-slate-50',
        borderColor: 'border-slate-200',
        textColor: 'text-slate-700',
        icon: Building2
      };
    default:
      return {
        bgColor: 'bg-gray-400',
        ringColor: 'ring-gray-100',
        lightBg: 'bg-gray-50',
        borderColor: 'border-gray-200',
        textColor: 'text-gray-600',
        icon: CheckCircle
      };
  }
};

// ============================================
// TIMELINE EVENT ITEM
// ============================================

interface TimelineEventItemProps {
  event: HistoryEventDto;
  isLast: boolean;
  riskId?: string;
  borrowerProjectId?: string;
}

const TimelineEventItem: React.FC<TimelineEventItemProps> = ({
  event,
  isLast,
  riskId,
  borrowerProjectId
}) => {
  const { t } = useTranslation(NsKey.Common);
  const priorityStyles = getPriorityStyles(event.priority);
  // Use iconKey from API if provided, otherwise fall back to priority-based icon
  const Icon = event.iconKey ? getIconByKey(event.iconKey, priorityStyles.icon) : priorityStyles.icon;
  // « Nouveau » : même droit que les pastilles des listes (rien en vue Plateforme)
  const canNotifBadges = useRoleCan(Capability.ListNotificationBadges);
  const isNew = event.isNew && canNotifBadges;

  return (
    <div className="relative flex gap-4">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-gradient-to-b from-gray-200 to-gray-100" />
      )}

      {/* Icon */}
      <div className="relative">
        <div
          className={clsx(
            'relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
            priorityStyles.bgColor,
            'ring-4',
            priorityStyles.ringColor,
            isNew && 'animate-pulse'
          )}
        >
          <Icon className="w-4 h-4 text-white" />
        </div>
        {/* New indicator dot */}
        {isNew && (
          <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-primary rounded-full border-2 border-white z-20" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div
          className={clsx(
            'relative rounded-lg border p-4 shadow-sm transition-all duration-200',
            isNew ? 'bg-primary/5 border-primary/30 ring-1 ring-primary/20' : priorityStyles.lightBg,
            !isNew && priorityStyles.borderColor
          )}
        >
          {/* New badge - absolute positioned */}
          {isNew && (
            <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-primary text-white rounded">
              {t('ACTIVITY_TIMELINE.NEW_BADGE')}
            </span>
          )}
          <div className="flex items-start justify-between gap-2">
            <h4 className={clsx(
              'font-medium text-gray-900',
              isNew && 'pr-16'
            )}>
              {event.title}
            </h4>
            <span className={clsx(
              'text-xs text-gray-500 whitespace-nowrap bg-white/60 px-2 py-0.5 rounded-full flex-shrink-0',
              isNew && 'mt-6'
            )}>
              {formatRelativeTime(event.createdAt)}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-700">
            <RenderedMessage
              message={event.message}
              entities={event.entities}
              segments={event.segments}
              riskId={riskId}
              borrowerProjectId={borrowerProjectId}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================
// TIMELINE GROUP ITEM
// ============================================

interface TimelineGroupItemProps {
  group: HistoryEventGroupDto;
  isLast: boolean;
  riskId?: string;
  borrowerProjectId?: string;
}

const TimelineGroupItem: React.FC<TimelineGroupItemProps> = ({
  group,
  isLast,
  riskId,
  borrowerProjectId
}) => {
  const { t } = useTranslation(NsKey.Common);
  const [isExpanded, setIsExpanded] = useState(false);
  const priorityStyles = getPriorityStyles(group.priority);
  const entityStyles = getEntityStyles(group.entityType);

  // Use entity styles for non-major events, priority styles for major
  const styles = group.isMajor ? priorityStyles : entityStyles;
  // Use iconKey from API if provided, otherwise fall back to style-based icon
  const Icon = group.iconKey ? getIconByKey(group.iconKey, styles.icon) : styles.icon;
  // « Nouveau » : même droit que les pastilles des listes (rien en vue Plateforme)
  const canNotifBadges = useRoleCan(Capability.ListNotificationBadges);
  const isNew = group.isNew && canNotifBadges;

  const toggleExpand = () => {
    if (!group.isMajor && group.details.length > 0) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className="relative flex gap-4">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-gradient-to-b from-gray-200 to-gray-100" />
      )}

      {/* Icon */}
      <div className="relative">
        <div
          className={clsx(
            'relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
            group.isMajor ? styles.bgColor : 'bg-white border-2',
            group.isMajor ? 'ring-4' : '',
            group.isMajor ? styles.ringColor : styles.borderColor,
            isNew && 'animate-pulse'
          )}
        >
          <Icon
            className={clsx(
              'w-4 h-4',
              group.isMajor ? 'text-white' : styles.textColor
            )}
          />
        </div>
        {/* New indicator dot */}
        {isNew && (
          <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-primary rounded-full border-2 border-white z-20" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div
          className={clsx(
            'relative rounded-lg border p-4 shadow-sm transition-all duration-200',
            isNew ? 'bg-primary/5 border-primary/30 ring-1 ring-primary/20' : (group.isMajor ? styles.lightBg : 'bg-white'),
            !isNew && (group.isMajor ? styles.borderColor : 'border-gray-200'),
            !group.isMajor &&
              group.details.length > 0 &&
              'cursor-pointer hover:border-gray-300 hover:shadow-md'
          )}
          onClick={toggleExpand}
        >
          {/* New badge - absolute positioned */}
          {isNew && (
            <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-primary text-white rounded z-10">
              {t('ACTIVITY_TIMELINE.NEW_BADGE')}
            </span>
          )}
          <div className="flex items-start justify-between gap-2">
            <div className={clsx('flex-1', isNew && 'pr-16')}>
              <div className="flex items-center gap-2">
                <h4
                  className={clsx(
                    'font-medium',
                    group.isMajor ? 'text-gray-900' : 'text-gray-800'
                  )}
                >
                  {group.title}
                </h4>
                {!group.isMajor && group.count > 1 && (
                  <span
                    className={clsx(
                      'px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0',
                      styles.lightBg,
                      styles.textColor
                    )}
                  >
                    {group.count}
                  </span>
                )}
              </div>
              {group.entityLabel && !group.isMajor && (
                <p className={clsx('text-xs mt-0.5', styles.textColor)}>
                  {group.entityLabel}
                </p>
              )}
            </div>
            <div className={clsx(
              'flex items-center gap-2 flex-shrink-0',
              isNew && 'mt-6'
            )}>
              <span className="text-xs text-gray-500 whitespace-nowrap bg-gray-50 px-2 py-0.5 rounded-full">
                {formatRelativeTime(group.createdAt)}
              </span>
              {!group.isMajor && group.details.length > 0 && (
                <button
                  className={clsx(
                    'p-1 rounded transition-colors',
                    isExpanded ? styles.lightBg : 'hover:bg-gray-100'
                  )}
                >
                  {isExpanded ? (
                    <ChevronUp className={clsx('w-4 h-4', styles.textColor)} />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              )}
            </div>
          </div>

          {group.message && (
            <p className="mt-1 text-sm text-gray-700">
              <RenderedMessage
                message={group.message}
                entities={group.entities}
                segments={group.segments}
                riskId={riskId}
                borrowerProjectId={borrowerProjectId}
              />
            </p>
          )}

          {/* Expanded details */}
          {isExpanded && group.details.length > 0 && (
            <div
              className={clsx(
                'mt-3 pt-3 border-t space-y-2',
                styles.borderColor
              )}
            >
              {group.details.map((detail) => (
                <TimelineDetailItem
                  key={detail.id}
                  detail={detail}
                  entityStyles={styles}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// TIMELINE DETAIL ITEM
// ============================================

interface TimelineDetailItemProps {
  detail: HistoryModificationDetailDto;
  entityStyles?: ReturnType<typeof getEntityStyles>;
}

const TimelineDetailItem: React.FC<TimelineDetailItemProps> = ({
  detail,
  entityStyles
}) => {
  const dotColor = entityStyles?.bgColor || 'bg-gray-400';

  return (
    <div className="flex items-start gap-2 text-sm">
      <div
        className={clsx('w-1.5 h-1.5 mt-1.5 rounded-full flex-shrink-0', dotColor)}
      />
      <div className="flex-1">
        <span className="font-medium text-gray-800">{detail.title}</span>
        <span className="text-gray-400 mx-1">—</span>
        <span className="text-gray-600">{detail.message}</span>
        <span className="text-xs text-gray-400 ml-2">
          {formatRelativeTime(detail.createdAt)}
        </span>
      </div>
    </div>
  );
};

// ============================================
// SKELETON LOADER
// ============================================

const TimelineSkeleton: React.FC = () => {
  return (
    <div className="space-y-4 animate-pulse">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex gap-4">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-200 to-gray-300" />
          <div className="flex-1">
            <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg p-4 space-y-2 border border-gray-200">
              <div className="h-4 bg-gray-200 rounded w-1/3" />
              <div className="h-3 bg-gray-200 rounded w-2/3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// EMPTY STATE
// ============================================

const EmptyState: React.FC = () => {
  const { t } = useTranslation(NsKey.Common);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center mb-4">
        <FileText className="w-8 h-8 text-primary/40" />
      </div>
      <p className="text-gray-500 text-sm">
        {t('ACTIVITY_TIMELINE.EMPTY_STATE')}
      </p>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

interface ActivityTimelineProps {
  riskId?: string;
  borrowerProjectId?: string;
  /** Contrat collectif porteur — bascule sur l'endpoint historique contrat de groupe. */
  groupContractId?: string;
}

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  riskId,
  borrowerProjectId,
  groupContractId
}) => {
  const { t } = useTranslation(NsKey.Common);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    events,
    groups,
    granularity,
    setGranularity,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    totalCount
  } = useActivityTimeline({
    riskId,
    borrowerProjectId,
    groupContractId,
    initialGranularity: GranularityLevelEnum.Standard
  });

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100;

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      loadMore();
    }
  }, [isLoadingMore, hasMore, loadMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const isStandardLevel = granularity === GranularityLevelEnum.Standard;
  const items = isStandardLevel ? groups : events;
  const hasItems = items.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header with granularity selector */}
      <div className="flex items-center justify-between px-1 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-semibold text-gray-900">
            {totalCount}
          </span>
          <span className="text-sm text-gray-500">
            {t('ACTIVITY_TIMELINE.EVENTS_COUNT')}
          </span>
        </div>
        <GranularitySelector value={granularity} onChange={setGranularity} />
      </div>

      {/* Timeline content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto py-4 pr-2"
      >
        {isLoading ? (
          <TimelineSkeleton />
        ) : !hasItems ? (
          <EmptyState />
        ) : (
          <div className="space-y-0">
            {isStandardLevel
              ? groups.map((group, index) => (
                  <TimelineGroupItem
                    key={group.id}
                    group={group}
                    isLast={index === groups.length - 1}
                    riskId={riskId}
                    borrowerProjectId={borrowerProjectId}
                  />
                ))
              : events.map((event, index) => (
                  <TimelineEventItem
                    key={event.id}
                    event={event}
                    isLast={index === events.length - 1}
                    riskId={riskId}
                    borrowerProjectId={borrowerProjectId}
                  />
                ))}

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* End of list indicator */}
            {!hasMore && hasItems && (
              <div className="text-center py-4">
                <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
                  {t('ACTIVITY_TIMELINE.END_OF_LIST')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;
